import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Heart, MessageCircle, Share2, Flag, Briefcase, Wrench, Users, Settings, Bookmark, Plus, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoFeedHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [adsShown, setAdsShown] = useState(0);
  const observerTarget = useRef(null);
  
  // Composer state
  const [composerText, setComposerText] = useState('');
  const [postType, setPostType] = useState('PERSONAL');
  const [posting, setPosting] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const [comments, setComments] = useState({});

  useEffect(() => {
    fetchPosts(true);
    fetchAds();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchPosts(false);
        }
      },
      { threshold: 0.5 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, skip]);

  const fetchPosts = async (reset = false) => {
    setLoading(true);
    try {
      const currentSkip = reset ? 0 : skip;
      const response = await axios.get(`${API}/feed/posts?skip=${currentSkip}&limit=20`);
      const newPosts = response.data.posts || [];
      
      if (reset) {
        setPosts(newPosts);
        setSkip(20);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
        setSkip(prev => prev + 20);
      }
      
      setHasMore(newPosts.length === 20);
    } catch (error) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/feed/ads`);
      setAds(response.data.ads || []);
    } catch (error) {
      console.error('Failed to load ads');
    }
  };

  const handleCreatePost = async () => {
    if (!user) {
      toast.error('Please login to post');
      navigate('/login');
      return;
    }
    
    if (!composerText.trim()) {
      toast.error('Please write something');
      return;
    }
    
    setPosting(true);
    try {
      await axios.post(`${API}/feed/posts?author_mode=PERSONAL&post_type=${postType}&text=${encodeURIComponent(composerText)}`);
      toast.success('Posted!');
      setComposerText('');
      fetchPosts(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!user) {
      toast.error('Please login');
      return;
    }
    try {
      await axios.post(`${API}/feed/posts/${postId}/like`);
      setPosts(prev => prev.map(p => p.id === postId ? {...p, like_count: p.like_count + 1} : p));
    } catch (error) {
      console.error(error);
    }
  };

  const toggleComments = async (postId) => {
    if (!showComments[postId]) {
      // Fetch comments
      try {
        const response = await axios.get(`${API}/feed/posts/${postId}/comments`);
        setComments(prev => ({...prev, [postId]: response.data.comments || []}));
      } catch (error) {
        console.error(error);
      }
    }
    setShowComments(prev => ({...prev, [postId]: !prev[postId]}));
  };

  const handleAddComment = async (postId) => {
    if (!user) {
      toast.error('Please login');
      return;
    }
    if (!commentText[postId]?.trim()) return;
    
    try {
      await axios.post(`${API}/feed/posts/${postId}/comments?text=${encodeURIComponent(commentText[postId])}`);
      setCommentText(prev => ({...prev, [postId]: ''}));
      setPosts(prev => prev.map(p => p.id === postId ? {...p, comment_count: p.comment_count + 1} : p));
      // Refresh comments
      const response = await axios.get(`${API}/feed/posts/${postId}/comments`);
      setComments(prev => ({...prev, [postId]: response.data.comments || []}));
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left Sidebar - 25% */}
          <div className="lg:col-span-3 space-y-4">
            {user && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                      {user.avatar_url ? <img src={user.avatar_url} className="w-full h-full rounded-full object-cover" alt={user.full_name} /> : user.full_name[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold">{user.full_name}</h3>
                      <p className="text-xs text-muted-foreground">{user.role}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/me/saved')}>
                      <Bookmark className="w-4 h-4 mr-2" />
                      Saved Items
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/profile')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <Button size="sm" className="w-full justify-start" onClick={() => navigate('/tradies/browse')}>
                    <Wrench className="w-4 h-4 mr-2" />
                    Find Tradies
                  </Button>
                  <Button size="sm" className="w-full justify-start" onClick={() => navigate('/jobs/browse')}>
                    <Briefcase className="w-4 h-4 mr-2" />
                    Browse Jobs
                  </Button>
                  <Button size="sm" className="w-full justify-start" onClick={() => navigate('/portal/talent')}>
                    <Users className="w-4 h-4 mr-2" />
                    Find Talent
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Center Feed - 50% */}
          <div className="lg:col-span-6 space-y-4">
            {/* Composer */}
            {user && (
              <Card>
                <CardContent className="p-4">
                  <Textarea
                    placeholder="What's on your mind?"
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    rows={3}
                    className="mb-3"
                  />
                  <div className="flex items-center justify-between">
                    <select
                      value={postType}
                      onChange={(e) => setPostType(e.target.value)}
                      className="px-3 py-1 border rounded text-sm"
                    >
                      <option value="PERSONAL">Personal</option>
                      <option value="WORK">Work</option>
                      <option value="HIRING">Hiring</option>
                      <option value="PROMOTION">Promotion</option>
                    </select>
                    <Button onClick={handleCreatePost} disabled={posting} size="sm">
                      {posting ? 'Posting...' : 'Post'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Feed */}
            {loading && posts.length === 0 ? (
              <div className="text-center py-20">Loading feed...</div>
            ) : (
              <div className="space-y-4">
                {posts.map((post, idx) => {
                  const shouldShowAd = idx > 0 && idx % 6 === 0 && adsShown < 3 && ads.length > 0;
                  const ad = shouldShowAd ? ads[adsShown % ads.length] : null;
                  
                  return (
                    <React.Fragment key={post.id}>
                      {ad && (() => { setAdsShown(prev => prev + 1); return (
                        <Card className="border-2 border-blue-200 dark:border-blue-800">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-xs">Sponsored</Badge>
                            </div>
                            <h3 className="font-bold text-lg mb-1">{ad.title}</h3>
                            <p className="text-sm text-muted-foreground mb-3">{ad.subtitle}</p>
                            <Button size="sm" onClick={() => navigate(ad.cta_url)}>{ad.cta_text}</Button>
                          </CardContent>
                        </Card>
                      ); })()}
                      
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                              {post.author?.avatar_url ? <img src={post.author.avatar_url} className="w-full h-full rounded-full object-cover" alt={post.author?.full_name} /> : post.author?.full_name?.[0] || 'U'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{post.author?.full_name || 'User'}</span>
                                <Badge variant="outline" className="text-xs">{post.post_type}</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                          
                          <p className="text-foreground mb-3 whitespace-pre-wrap">{post.text}</p>
                          
                          {post.media_images && post.media_images.length > 0 && (
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              {post.media_images.slice(0, 4).map((img, imgIdx) => (
                                <img key={imgIdx} src={img} className="w-full h-32 object-cover rounded" alt="" />
                              ))}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 pt-3 border-t">
                            <button onClick={() => handleLike(post.id)} className="flex items-center gap-1 text-muted-foreground hover:text-red-500">
                              <Heart className="w-4 h-4" />
                              <span className="text-sm">{post.like_count || 0}</span>
                            </button>
                            <button onClick={() => toggleComments(post.id)} className="flex items-center gap-1 text-muted-foreground hover:text-blue-500">
                              <MessageCircle className="w-4 h-4" />
                              <span className="text-sm">{post.comment_count || 0}</span>
                            </button>
                            <button className="flex items-center gap-1 text-muted-foreground hover:text-blue-500">
                              <Share2 className="w-4 h-4" />
                            </button>
                          </div>
                          
                          {/* Comments Section */}
                          {showComments[post.id] && (
                            <div className="mt-3 pt-3 border-t space-y-3">
                              {comments[post.id]?.map(comment => (
                                <div key={comment.id} className="flex gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                                    {comment.author?.full_name?.[0] || 'U'}
                                  </div>
                                  <div className="flex-1">
                                    <div className="bg-muted rounded-lg p-2">
                                      <span className="font-semibold text-sm">{comment.author?.full_name}</span>
                                      <p className="text-sm">{comment.text}</p>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">{new Date(comment.created_at).toLocaleString()}</p>
                                  </div>
                                </div>
                              ))}
                              
                              {user && (
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    placeholder="Write a comment..."
                                    value={commentText[post.id] || ''}
                                    onChange={(e) => setCommentText(prev => ({...prev, [post.id]: e.target.value}))}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                  />
                                  <Button size="sm" onClick={() => handleAddComment(post.id)}>Post</Button>
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </React.Fragment>
                  );
                })}
                
                <div ref={observerTarget} className="h-10 flex items-center justify-center">
                  {loading && <div className="text-muted-foreground">Loading more...</div>}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar - 25% */}
          <div className="lg:col-span-3 space-y-4">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Trending
                </h3>
                <p className="text-sm text-muted-foreground">Popular services and jobs coming soon</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoFeedHome;
